import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface GooglePlacesTypesData {
  types: string[];
  lastUpdated?: Date;
  fromCache: boolean;
}

interface UseGooglePlacesTypesReturn {
  data: GooglePlacesTypesData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Cache TTL: 24 hours (types rarely change)
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const fetchCachedTypes = async (businessId: string, googlePlaceId: string): Promise<GooglePlacesTypesData | null> => {
  try {
    const { data: business, error: dbError } = await supabase
      .from('businesses')
      .select('cached_google_types, google_types_cache_updated_at')
      .eq('id', businessId)
      .single();

    if (dbError || !business) {
      return null;
    }

    if (!business.google_types_cache_updated_at) {
      return null; // No cache available
    }

    const cacheAge = new Date().getTime() - new Date(business.google_types_cache_updated_at).getTime();
    const isCacheValid = cacheAge < CACHE_TTL_MS;

    if (!isCacheValid) {
      return null; // Cache expired
    }

    return {
      types: (business.cached_google_types as string[]) || [],
      lastUpdated: new Date(business.google_types_cache_updated_at),
      fromCache: true
    };
  } catch (err) {
    return null;
  }
};

const fetchFreshTypes = async (businessId: string, googlePlaceId: string): Promise<GooglePlacesTypesData> => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    throw new Error('Google Maps API key not configured');
  }

  const url = `https://places.googleapis.com/v1/places/${googlePlaceId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'types'
    }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Google Place not found');
    } else if (response.status === 403) {
      throw new Error('API access denied - check your API key permissions');
    } else {
      throw new Error(`Google Places API error: ${response.status}`);
    }
  }

  const responseData = await response.json();

  const freshData: GooglePlacesTypesData = {
    types: responseData.types || [],
    lastUpdated: new Date(),
    fromCache: false
  };

  // Update cache in background
  supabase
    .from('businesses')
    .update({
      cached_google_types: freshData.types,
      google_types_cache_updated_at: freshData.lastUpdated!.toISOString()
    })
    .eq('id', businessId)
    .then(({ error }) => {
      if (error) {
        console.error('Failed to update Google types cache:', error);
      }
    });

  return freshData;
};

const fetchGooglePlacesTypes = async (businessId: string, googlePlaceId: string): Promise<GooglePlacesTypesData> => {
  // Try cache first
  const cachedData = await fetchCachedTypes(businessId, googlePlaceId);
  if (cachedData) {
    return cachedData;
  }

  // Fetch fresh data from Google API
  return fetchFreshTypes(businessId, googlePlaceId);
};

export const useGooglePlacesTypes = (businessId?: string | null, googlePlaceId?: string | null): UseGooglePlacesTypesReturn => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['googlePlacesTypes', googlePlaceId],
    queryFn: () => fetchGooglePlacesTypes(businessId!, googlePlaceId!),
    enabled: !!(businessId && googlePlaceId),
    staleTime: CACHE_TTL_MS, // Consider data fresh for 24 hours
    gcTime: CACHE_TTL_MS, // Keep in cache for 24 hours
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    data: data || null,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch: () => { refetch(); }
  };
};

export default useGooglePlacesTypes;
